import { Fragment, memo, useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import {
    CategoryScale,
    Chart as ChartJS,
    type Chart,
    type ChartData,
    type ChartDataset,
    type ChartOptions,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    type ScriptableContext,
    type ScriptableLineSegmentContext,
    Title,
    Tooltip,
} from 'chart.js';

import { cleanupCustomTooltip, createCustomTooltip } from '@/shared/lib/chart';
import { useTheme, useXLMediaQuery } from '@/shared/lib/hooks';
import { roundToDecimals } from '@/shared/lib/math';
import { Card } from '@/shared/ui/card';
import { Progress } from '@/shared/ui/progress';
import { TitleWithHelp } from '@/shared/ui/title-with-help';

import { getResolvedPatternColors } from '../../lib/pattern-colors';
import {
    createDataPoints,
    getDirectionalProperty,
    getMaxValue,
} from '../../lib/pattern-manipulator';
import type { Pattern } from '../../model/types/pattern';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);

export interface PatternReturnsChartProps {
    patterns: Pattern | Pattern[];
    title: string;
    chartId: string;
    colorsById?: Record<string, string>;
    isAggregationMode?: boolean;
}

interface CustomFillOptions {
    target: 'origin' | 'start' | 'end' | number | string | undefined;
    above: (ctx: ScriptableContext<'line'>) => string | CanvasGradient;
    below: (ctx: ScriptableContext<'line'>) => string | CanvasGradient;
}

const GREEN = '#52C41A' as const;
const RED = '#D32029' as const;
const FILL_GREEN_TOP = 'rgba(82, 196, 26, 0.8)' as const;
const FILL_GREEN_MID = 'rgba(82, 196, 26, 0.2)' as const;
const FILL_GREEN_BOTTOM = 'rgba(82, 196, 26, 0)' as const;
const FILL_RED_TOP = 'rgba(211, 32, 41, 0.8)' as const;
const FILL_RED_MID = 'rgba(211, 32, 41, 0.2)' as const;
const FILL_RED_BOTTOM = 'rgba(211, 32, 41, 0)' as const;
const FALLBACK_SEGMENT_COLOR = 'rgba(255, 255, 255, 0.2)' as const;
const DEFAULT_BORDER_COLOR = 'rgba(128, 128, 128, 0.5)' as const;
const POINT_BORDER_COLOR = '#1F1F1F' as const;

const getPointBgColor = (context: { raw: unknown }): string => {
    const value = context.raw as number;

    return value < 0 ? RED : GREEN;
};

const segmentBorderColor = (
    ctx: ScriptableLineSegmentContext,
): string | CanvasGradient => {
    if (!ctx.p0.parsed || !ctx.p1.parsed) {
        return FALLBACK_SEGMENT_COLOR;
    }

    const p0Value = ctx.p0.parsed.y;
    const p1Value = ctx.p1.parsed.y;

    if (p0Value < 0 && p1Value < 0) {
        return RED;
    }

    if (p0Value >= 0 && p1Value >= 0) {
        return GREEN;
    }

    const canvasCtx = (ctx as { chart?: Chart }).chart?.ctx;

    if (!canvasCtx) {
        return p0Value >= 0 ? GREEN : RED;
    }

    const gradient = canvasCtx.createLinearGradient(ctx.p0.x, 0, ctx.p1.x, 0);

    if (p0Value >= 0 && p1Value < 0) {
        gradient.addColorStop(0, GREEN);
        gradient.addColorStop(1, RED);
    } else {
        gradient.addColorStop(0, RED);
        gradient.addColorStop(1, GREEN);
    }

    return gradient;
};

const lineBorderColor = (ctx: ScriptableContext<'line'>): string => {
    if (!ctx.parsed) {
        return DEFAULT_BORDER_COLOR;
    }

    return ctx.parsed.y >= 0 ? '#4ADE80' : '#EF4444';
};

const aboveFillGradient = (
    ctx: ScriptableContext<'line'>,
): string | CanvasGradient => {
    if (!ctx.chart.chartArea) {
        return FILL_GREEN_MID;
    }

    const { ctx: canvasCtx, chartArea } = ctx.chart;
    const gradient = canvasCtx.createLinearGradient(
        0,
        chartArea.top,
        0,
        chartArea.bottom,
    );

    gradient.addColorStop(0, FILL_GREEN_TOP);
    gradient.addColorStop(0.6, FILL_GREEN_MID);
    gradient.addColorStop(1, FILL_GREEN_BOTTOM);

    return gradient;
};

const belowFillGradient = (
    ctx: ScriptableContext<'line'>,
): string | CanvasGradient => {
    if (!ctx.chart.chartArea) {
        return FILL_RED_MID;
    }

    const { ctx: canvasCtx, chartArea } = ctx.chart;
    const gradient = canvasCtx.createLinearGradient(
        0,
        chartArea.top,
        0,
        chartArea.bottom,
    );

    gradient.addColorStop(0, FILL_RED_BOTTOM);
    gradient.addColorStop(0.6, FILL_RED_MID);
    gradient.addColorStop(1, FILL_RED_TOP);

    return gradient;
};

interface StatsRow {
    label: string;
    value: number;
    progress: number;
    variant: 'green' | 'red';
}

const calculateProgress = (
    value: number,
    threshold: number,
    showProgress: boolean,
): number => {
    if (!showProgress) return 0;

    return Math.max(0, Math.min(100, (value / threshold) * 100));
};

const addDrawdownMaxRow = (
    rows: StatsRow[],
    pattern: Partial<Pattern>,
    threshold: number,
    showProgress: boolean,
    t: (key: string) => string,
): void => {
    const value = Number(pattern.price_drawdown_post_max);

    if (!Number.isFinite(value) || pattern.price_drawdown_post_max == null) {
        return;
    }

    const progress = calculateProgress(value, threshold, showProgress);

    rows.push({
        label: t('patternStats.drawdownMax'),
        value,
        progress,
        variant: 'red',
    });
};

const addAverageDrawdownRow = (
    rows: StatsRow[],
    pattern: Partial<Pattern>,
    threshold: number,
    showProgress: boolean,
    t: (key: string) => string,
): void => {
    const value = Number(pattern.price_drawdown_post_mean);

    if (!Number.isFinite(value) || pattern.price_drawdown_post_mean == null) {
        return;
    }

    const progress = calculateProgress(value, threshold, showProgress);

    rows.push({
        label: t('patternStats.averageDrawdown'),
        value,
        progress,
        variant: 'red',
    });
};

const addMaxProfitRow = (
    rows: StatsRow[],
    pattern: Partial<Pattern>,
    threshold: number,
    showProgress: boolean,
    t: (key: string) => string,
): void => {
    try {
        const maxProfit = getMaxValue(pattern, 'price_reg_post_list');

        if (Number.isFinite(maxProfit)) {
            const progress = calculateProgress(
                maxProfit,
                threshold,
                showProgress,
            );

            rows.push({
                label: t('patternStats.maxProfit'),
                value: maxProfit,
                progress,
                variant: 'green',
            });
        }
    } catch {
        // ignore parsing errors, hide the row
    }
};

const addAverageProfitRow = (
    rows: StatsRow[],
    pattern: Partial<Pattern>,
    threshold: number,
    showProgress: boolean,
    t: (key: string) => string,
): void => {
    const value = Number(pattern.price_max_variation_post_mean);

    if (
        !Number.isFinite(value) ||
        pattern.price_max_variation_post_mean == null
    ) {
        return;
    }

    const progress = calculateProgress(value, threshold, showProgress);

    rows.push({
        label: t('patternStats.averageProfit'),
        value,
        progress,
        variant: 'green',
    });
};

const buildStatsRows = (
    pattern: Partial<Pattern>,
    t: (key: string) => string,
    showProgress: boolean = true,
): StatsRow[] => {
    const threshold = pattern.price_threshold_post_mean;
    const hasValidThreshold =
        threshold && Number.isFinite(threshold) && threshold > 0;

    if (!hasValidThreshold) return [];

    const rows: StatsRow[] = [];

    addDrawdownMaxRow(rows, pattern, threshold, showProgress, t);
    addAverageDrawdownRow(rows, pattern, threshold, showProgress, t);
    addMaxProfitRow(rows, pattern, threshold, showProgress, t);
    addAverageProfitRow(rows, pattern, threshold, showProgress, t);

    return rows;
};

// Helper to check if all patterns have the same threshold
const haveEqualThresholds = (patterns: Pattern[]): boolean => {
    if (patterns.length <= 1) return true;

    const firstThreshold = patterns[0]?.price_threshold_post_mean;

    return patterns.every(
        (p) =>
            Math.abs(
                (p.price_threshold_post_mean || 0) - (firstThreshold || 0),
            ) < 0.0001,
    );
};

// Helper to aggregate stats from multiple entries using frequency weighting
const aggregateStats = (
    entries: Array<{ stats: StatsRow[]; displayName: string }>,
    showProgress: boolean,
): StatsRow[] => {
    if (entries.length === 0) return [];

    // Group stats by label
    const statsByLabel = new Map<string, StatsRow[]>();

    entries.forEach((entry) => {
        entry.stats.forEach((stat) => {
            if (!statsByLabel.has(stat.label)) {
                statsByLabel.set(stat.label, []);
            }

            statsByLabel.get(stat.label)!.push(stat);
        });
    });

    // Aggregate each stat group
    const aggregatedStats: StatsRow[] = [];

    statsByLabel.forEach((stats, label) => {
        // Calculate weighted average (assuming equal weight for now, could be enhanced with frequency)
        const totalValue = stats.reduce((sum, stat) => sum + stat.value, 0);
        const avgValue = totalValue / stats.length;

        const totalProgress = stats.reduce(
            (sum, stat) => sum + stat.progress,
            0,
        );
        const avgProgress = showProgress ? totalProgress / stats.length : 0;

        // Use the variant from the first stat (they should all be the same for same label)
        const variant = stats[0]?.variant || 'green';

        aggregatedStats.push({
            label,
            value: avgValue,
            progress: avgProgress,
            variant,
        });
    });

    return aggregatedStats;
};

const createPatternEntry = (
    p: Pattern,
    shouldShowProgress: boolean,
    t: (key: string) => string,
) => {
    const id = String(
        (p as Partial<Pattern>)._id ??
            (p as Partial<Pattern>).pattern_idx ??
            (p as Partial<Pattern>).pattern_name ??
            'pattern',
    );

    const series = getDirectionalProperty<number[]>(
        p,
        'ts_profit_long_post_mean_of_lists',
        'ts_profit_short_post_mean_of_lists',
    );

    const labels = createDataPoints(series).map((d) => d.timeLabel);

    const displayName =
        (p as Partial<Pattern>).pattern_name ??
        (p as Partial<Pattern>).unique_id ??
        id;

    return {
        id,
        displayName,
        series,
        labels,
        color: (p as Partial<Pattern>).color,
        stats: buildStatsRows(p, t, shouldShowProgress),
    };
};

const buildStatsRowsData = (
    entries: ReturnType<typeof createPatternEntry>[],
    isAggregationMode: boolean,
    isMulti: boolean,
    shouldShowProgress: boolean,
) => {
    if (isAggregationMode && entries.length > 1) {
        // In aggregation mode, aggregate the stats but keep all chart lines
        const aggregatedStats = aggregateStats(entries, shouldShowProgress);
        const aggregatedName = entries.map((e) => e.displayName).join(', ');

        return [
            {
                id: 'aggregated',
                displayName: aggregatedName,
                stats: aggregatedStats,
            },
        ];
    }

    if (isMulti && !isAggregationMode) {
        return entries;
    }

    return [{ id: '', displayName: '', stats: entries[0]?.stats ?? [] }];
};

const getContainerStyles = () =>
    ({
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 12,
    }) as const;

const getChartStyles = (isAggregationMode: boolean) => ({
    minHeight: 300,
    width: isAggregationMode ? '100%' : undefined,
});

const getStatsContainerStyles = (
    isXL: boolean,
    isMulti: boolean,
    isAggregationMode: boolean,
) =>
    ({
        display: isXL && isMulti && !isAggregationMode ? 'grid' : 'flex',
        gridTemplateColumns:
            isXL && isMulti && !isAggregationMode ? '1fr 1fr' : undefined,
        flexDirection:
            isXL && isMulti && !isAggregationMode ? undefined : 'column',
        gap: isXL ? 16 : 8,
        width: isAggregationMode ? '100%' : 'auto',
        height: 'auto',
        maxHeight: 400,
        overflow: 'visible',
        flexShrink: 0,
    }) as const;

export const PatternReturnsChart = memo(
    ({
        patterns,
        title,
        chartId,
        colorsById,
        isAggregationMode = false,
    }: PatternReturnsChartProps) => {
        const { t } = useTranslation('pattern-studio');
        const isXL = useXLMediaQuery();
        const isDarkTheme = useTheme()[2];

        useEffect(
            () => () => {
                cleanupCustomTooltip();
            },
            [],
        );

        const patternList = useMemo(
            () => (Array.isArray(patterns) ? patterns : [patterns]),
            [patterns],
        );

        const isMulti = patternList.length > 1;
        const resolvedColors = useMemo(
            () => getResolvedPatternColors(patternList, isMulti),
            [patternList, isMulti],
        );

        const shouldShowProgress = useMemo(() => {
            if (isAggregationMode) {
                // In aggregation mode, show progress only if all original patterns have equal thresholds
                return haveEqualThresholds(patternList);
            }

            return true; // In comparison mode, always show progress
        }, [isAggregationMode, patternList]);

        const entries = useMemo(
            () =>
                patternList.map((p) =>
                    createPatternEntry(p, shouldShowProgress, t),
                ),
            [patternList, shouldShowProgress, t],
        );

        const labels = entries[0]?.labels ?? [];

        const datasets = entries.map(
            ({ series, id, color, displayName }, idx) => {
                const appliedColor =
                    colorsById && colorsById[id]
                        ? colorsById[id]
                        : color ?? resolvedColors[idx];

                return {
                    label: displayName,
                    data: series,
                    tension: 0.4,
                    pointRadius: 2,
                    pointBackgroundColor: appliedColor ?? getPointBgColor,
                    pointBorderColor: POINT_BORDER_COLOR,
                    pointBorderWidth: 1,
                    borderWidth: 1,
                    segment: {
                        borderColor: appliedColor ?? segmentBorderColor,
                    },
                    backgroundColor: 'transparent',
                    borderColor: appliedColor ?? lineBorderColor,
                    fill: {
                        target: 'origin',
                        above: aboveFillGradient,
                        below: belowFillGradient,
                    } as CustomFillOptions,
                };
            },
        );

        const data: ChartData<'line'> = {
            labels,
            datasets: datasets as unknown as ChartDataset<
                'line',
                (number | null)[]
            >[],
        };

        const options: ChartOptions<'line'> = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: datasets.length > 1 },
                title: { display: false },
                tooltip: {
                    enabled: false,
                    external: createCustomTooltip({
                        isDark: isDarkTheme,
                        title,
                        formatItems: (tooltipModel) =>
                            tooltipModel.dataPoints.map((point) => {
                                const label = point.dataset.label ?? '';
                                const value = point.parsed.y;

                                return {
                                    color: point.dataset.borderColor as string,
                                    label,
                                    value: `${roundToDecimals(value)}%`,
                                };
                            }),

                        getBadgeVariant: (value) => {
                            if (value > 0) return 'positive';

                            if (value < 0) return 'negative';

                            return null;
                        },
                    }),
                },
            },
            scales: {
                x: {
                    grid: { display: false },
                },
                y: {
                    grid: { color: 'rgba(255,255,255,0.1)' },
                    ticks: {
                        callback: (v) => `${v}%`,
                    },
                },
            },
        };

        const statsRows = useMemo(
            () =>
                buildStatsRowsData(
                    entries,
                    isAggregationMode,
                    isMulti,
                    shouldShowProgress,
                ),
            [entries, isAggregationMode, isMulti, shouldShowProgress],
        );

        return (
            <Card
                isWithoutPadding
                title={
                    <TitleWithHelp
                        helpId="PATTERN_RETURNS_CHART"
                        title={title}
                    />
                }
            >
                <div style={getContainerStyles()}>
                    <div style={getChartStyles(isAggregationMode)}>
                        <Line
                            data={data}
                            id={chartId}
                            options={options}
                        />
                    </div>

                    <div
                        style={getStatsContainerStyles(
                            isXL,
                            isMulti,
                            isAggregationMode,
                        )}
                    >
                        {statsRows.map((patternData) => (
                            <div key={patternData.id || 'single'}>
                                {(isMulti || isAggregationMode) &&
                                    patternData.displayName && (
                                        <div
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 600,
                                                marginBottom: 8,
                                                color: 'rgba(255, 255, 255, 0.9)',
                                                borderBottom:
                                                    '1px solid rgba(255, 255, 255, 0.1)',
                                                paddingBottom: 4,
                                            }}
                                        >
                                            {patternData.displayName}
                                        </div>
                                    )}
                                {patternData.stats.length ? (
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr',
                                            gap: 6,
                                            marginBottom: isMulti ? 16 : 0,
                                        }}
                                    >
                                        {patternData.stats.map((r, index) => (
                                            <Fragment key={r.label}>
                                                <div>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent:
                                                                'space-between',
                                                            gap: 8,
                                                            marginBottom: 4,
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                opacity: 0.8,
                                                            }}
                                                        >
                                                            {r.label}
                                                        </span>
                                                        <span
                                                            style={{
                                                                color:
                                                                    r.variant ===
                                                                    'red'
                                                                        ? '#EF4444'
                                                                        : '#4ADE80',
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {roundToDecimals(
                                                                r.value,
                                                            )}
                                                            %
                                                        </span>
                                                    </div>
                                                    {shouldShowProgress && (
                                                        <Progress
                                                            suffix={t(
                                                                'patternStats.ofThreshold',
                                                            )}
                                                            value={r.progress}
                                                            variant={r.variant}
                                                        />
                                                    )}
                                                    {index <
                                                        patternData.stats
                                                            .length -
                                                            1 && (
                                                        <div
                                                            style={{
                                                                height: '1px',
                                                                backgroundColor:
                                                                    'rgba(255, 255, 255, 0.1)',
                                                                margin: '8px 0',
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </Fragment>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                </div>
            </Card>
        );
    },
);

PatternReturnsChart.displayName = 'PatternReturnsChart';
